import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
  resetPasswordDirect: (email, newPassword, confirmPassword) =>
    api.post('/auth/reset-password-direct', {
      email,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
};

// Modules API
export const modulesAPI = {
  getAll: () => api.get('/modules'),
  getOne: (id) => api.get(`/modules/${id}`),
  create: (data) => api.post('/modules', data),
  update: (id, data) => api.put(`/modules/${id}`, data),
  delete: (id) => api.delete(`/modules/${id}`),
  reorder: (moduleIds) => api.put('/modules/reorder', { module_ids: moduleIds }),
};

// Milestones API (large tasks shown on Roadmap / Milestones Board)
export const milestonesAPI = {
  getAll: (params) => api.get('/milestones', { params }),
  getOne: (id) => api.get(`/milestones/${id}`),
  create: (data) => api.post('/milestones', data),
  update: (id, data) => api.put(`/milestones/${id}`, data),
  delete: (id) => api.delete(`/milestones/${id}`),
  addComment: (milestoneId, content, parentId) => api.post(`/milestones/${milestoneId}/comments`, { content, parent_id: parentId }),
};

// Dev Tasks API (small task cards)
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  restaurantSubmit: (data) => api.post('/tasks/restaurant-submit', data),
  getComments: (id) => api.get(`/tasks/${id}/comments`),
  addComment: (id, content) => api.post(`/tasks/${id}/comments`, { content }),
};

// Release features API (weekly summaries on dev tasks)
export const releaseAPI = {
  listByTask: (taskId) => api.get('/release-summaries', { params: { task_id: taskId } }),
  listWeeks: (params) => api.get('/release-summaries/weeks', { params }),
  listItems: (params) => api.get('/release-summaries', { params }),
  create: (data) => api.post('/release-summaries', data),
  update: (id, data) => api.put(`/release-summaries/${id}`, data),
  delete: (id) => api.delete(`/release-summaries/${id}`),
  export: (format, params) => api.get('/release-summaries/export', { params: { format, ...params }, responseType: 'blob' }),
  pptx: (data) => api.post('/release-summaries/pptx', data, { responseType: 'blob' }),
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Restaurants API
export const restaurantsAPI = {
  getAll: () => api.get('/restaurants'),
  getStats: () => api.get('/restaurants/stats'),
  getOne: (id) => api.get(`/restaurants/${id}`),
  create: (data) => api.post('/restaurants', data),
  update: (id, data) => api.put(`/restaurants/${id}`, data),
  delete: (id) => api.delete(`/restaurants/${id}`),
  addModule: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/modules`, data),
  updateModule: (restaurantId, moduleId, data) => api.put(`/restaurants/${restaurantId}/modules/${moduleId}`, data),
  removeModule: (restaurantId, moduleId) => api.delete(`/restaurants/${restaurantId}/modules/${moduleId}`),
  getComments: (id) => api.get(`/restaurants/${id}/comments`),
  addComment: (id, content) => api.post(`/restaurants/${id}/comments`, { content }),
  deleteComment: (id, commentId) => api.delete(`/restaurants/${id}/comments/${commentId}`),
  exportProposal: (id) => api.get(`/restaurants/${id}/proposal.pdf`, { responseType: 'blob' }),
  uploadLogo: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/restaurants/${id}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (id) => api.delete(`/restaurants/${id}/logo`),
};

// Restaurant (CRM) Tasks API
export const restaurantTasksAPI = {
  getAll: (params) => api.get('/restaurant-tasks', { params }),
  create: (data) => api.post('/restaurant-tasks', data),
  update: (id, data) => api.put(`/restaurant-tasks/${id}`, data),
  delete: (id) => api.delete(`/restaurant-tasks/${id}`),
  getComments: (id) => api.get(`/restaurant-tasks/${id}/comments`),
  addComment: (id, content) => api.post(`/restaurant-tasks/${id}/comments`, { content }),
};

// Ideas API
export const ideasAPI = {
  getAll: (params) => api.get('/ideas', { params }),
  getOne: (id) => api.get(`/ideas/${id}`),
  create: (data) => api.post('/ideas', data),
  update: (id, data) => api.put(`/ideas/${id}`, data),
  analyze: (id) => api.post(`/ideas/${id}/analyze`),
  toTask: (id, moduleId) => api.post(`/ideas/${id}/to-task?module_id=${moduleId}`),
  addComment: (ideaId, content) => api.post(`/ideas/${ideaId}/comments`, { content }),
};

// Reports API
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// File Upload API
export const uploadFile = async (file, taskId = null, ideaId = null, devTaskId = null, restaurantId = null, restaurantTaskId = null, releaseSummaryId = null, isDescriptionMedia = false) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const params = new URLSearchParams();
  if (taskId) params.append('task_id', taskId);
  if (ideaId) params.append('idea_id', ideaId);
  if (devTaskId) params.append('dev_task_id', devTaskId);
  if (restaurantId) params.append('restaurant_id', restaurantId);
  if (restaurantTaskId) params.append('restaurant_task_id', restaurantTaskId);
  if (releaseSummaryId) params.append('release_summary_id', releaseSummaryId);
  if (isDescriptionMedia) params.append('is_description_media', 'true');
  
  return api.post(`/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getFileUrl = (path) => `${API_URL}/api/files/${path}`;

export const deleteAttachment = (id) => api.delete(`/attachments/${id}`);

export default api;
